import { useEffect, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist";

// Configure local worker via new URL() — Vite emits it as a separate asset,
// avoiding OOM from bundling, and guaranteeing version match with the library.
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.js",
  import.meta.url
).toString();

type PageSize = { width: number; height: number };

export function usePdfLoader(url: string) {
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageSize, setPageSize] = useState<PageSize | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!url) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      setPdf(null);
      setNumPages(0);
      setPageSize(null);

      try {
        console.log("[usePdfLoader] Loading PDF from URL length:", url.length);
        const loadingTask = pdfjsLib.getDocument(url);
        const pdfDoc = await loadingTask.promise;
        console.log("[usePdfLoader] PDF loaded, pages:", pdfDoc.numPages);

        if (cancelled) return;

        setPdf(pdfDoc);
        setNumPages(pdfDoc.numPages);

        const page1 = await pdfDoc.getPage(1);
        if (!cancelled) {
          const viewport = page1.getViewport({ scale: 1 });
          setPageSize({ width: viewport.width, height: viewport.height });
          setLoading(false);
        }
      } catch (err: unknown) {
        console.error("[usePdfLoader] Failed to load PDF:", err);
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [url]);

  return { pdf, numPages, pageSize, loading, error };
}
